import {
  CalendarClock,
  CircleCheck,
  CircleHelp,
  GraduationCap,
  History,
  Plane,
  Repeat,
  TriangleAlert,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import { useState } from 'react';

import type { PickerRequest } from '@/components/Overlays/context';
import { cn } from '@/design-system/lib/cn';
import { Avatar } from '@/design-system/ui/Avatar';
import { Button } from '@/design-system/ui/Button';
import { Dialog, DialogContent, DialogHeader } from '@/design-system/ui/Dialog';
import { SearchInput } from '@/design-system/ui/Input';
import {
  MONTH_SHORT,
  PERIOD_LABEL,
  formatShortDate,
  monthKey,
  normalize,
  parseDate,
  periodOf,
} from '@escala/domain/dates';
import {
  type Candidate,
  type CandidateTag,
  evaluateCandidates,
  monthlyCounts,
  quantityOf,
} from '@escala/domain/schedule';
import type { RoleRuleKind } from '@escala/domain/roleRules';
import { useStore } from '@/store/AppStore';

const TAGS: Record<CandidateTag, { label: string; className: string; icon: typeof CircleCheck }> = {
  available: {
    label: 'Disponível',
    className: 'bg-status-complete-bg text-status-complete',
    icon: CircleCheck,
  },
  unset: { label: 'Não configurado', className: 'bg-surface-muted text-ink-muted', icon: CircleHelp },
  scheduled: { label: 'Já escalado', className: 'bg-surface-muted text-ink-muted', icon: UserCheck },
  sex: { label: 'Restrição', className: 'bg-status-conflict-bg text-status-conflict', icon: TriangleAlert },
  unavailable: { label: 'Indisponível', className: 'bg-status-conflict-bg text-status-conflict', icon: X },
  blackout: { label: 'Ausente', className: 'bg-status-partial-bg text-status-partial', icon: Plane },
};

/**
 * Escolha de quem ocupa uma vaga (ou substitui alguém). Candidatos do melhor para o pior,
 * com o "Sugerido" no topo. Escolher quem viola regra abre a explicação e pede
 * confirmação — nunca bloqueia em silêncio.
 */
export default function MemberPicker({ request, onClose }: { request: PickerRequest; onClose: () => void }) {
  const { state, lookup, actions } = useStore();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Candidate | null>(null);

  const event = state.events.find((e) => e.id === request.eventId);
  const entry = lookup.roles.get(request.roleId);
  const month = event ? monthKey(event.date) : '';
  const candidates = event
    ? evaluateCandidates(event, request.roleId, lookup, {
        replaceId: request.replaceId,
        monthCounts: monthlyCounts(state.events, month),
        limit: state.settings.maxPerMonth,
      })
    : [];

  if (!event || !entry) return null;

  const replaced = request.replaceId ? lookup.members.get(request.replaceId) : null;
  const have = (event.slots[request.roleId] ?? []).length;
  const qty = quantityOf(event, entry.role);
  const isOver = !replaced && have >= qty;
  const q = normalize(query.trim());
  const visible = candidates.filter((c) => !q || normalize(c.member.name).includes(q));
  const monthLabel = MONTH_SHORT[parseDate(event.date).getMonth()];

  const confirmPick = (candidate: Candidate) => {
    actions.assign(event.id, request.roleId, candidate.member.id, request.replaceId);
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[min(660px,calc(100dvh-40px))] w-[min(470px,calc(100vw-24px))]">
        <DialogHeader
          bordered={false}
          title={replaced ? `Substituir ${replaced.name}` : `Adicionar em ${entry.role.name}`}
          description={`${event.title} · ${formatShortDate(event.date)} · ${PERIOD_LABEL[periodOf(event.time)].toLowerCase()}`}
          className="pb-3"
        />
        {/* Fora do cabeçalho, para ocupar a largura do diálogo (e não só a do título). */}
        {isOver ? (
          <p className="mx-5 mt-0 mb-2.5 rounded-control-sm border border-line-input bg-surface-subtle px-2.5 py-2 text-meta text-ink-secondary max-md:mx-4">
            {entry.role.name} já tem {qty} {qty === 1 ? 'pessoa' : 'pessoas'}, a quantidade padrão. Quem você
            escolher entra como extra.
          </p>
        ) : null}
        <div className="px-5 pb-2.5 max-md:px-4">
          <SearchInput
            aria-label="Buscar membro"
            placeholder="Buscar membro habilitado"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-3 pb-2">
          {visible.map((c) => {
            const tag = TAGS[c.tag];
            const TagIcon = tag.icon;
            const isSelected = selected?.member.id === c.member.id;

            return (
              <button
                key={c.member.id}
                type="button"
                aria-pressed={isSelected}
                onClick={() => (c.issue ? setSelected(c) : confirmPick(c))}
                className={cn(
                  'flex min-h-13 w-full items-center gap-2.5 rounded-control border-[1.5px] px-2.5 py-1.5 text-left hover:bg-surface-subtle',
                  isSelected ? 'border-primary-hover-line bg-primary-faint' : 'border-transparent bg-white'
                )}
              >
                <Avatar name={c.member.name} photo={c.member.photo} size="lg" />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="text-control font-semibold">{c.member.name}</span>
                  <span className="text-caption text-ink-subtle">
                    {c.monthCount} {c.monthCount === 1 ? 'escala' : 'escalas'} em {monthLabel}.
                    {c.suggested ? (
                      <span className="ml-1.5 font-semibold text-primary-hover">· Sugerido</span>
                    ) : null}
                  </span>
                  <RuleBadges
                    candidate={c}
                    partnerName={c.partnerId ? (lookup.members.get(c.partnerId)?.name ?? null) : null}
                  />
                </span>
                <span
                  className={cn(
                    'inline-flex h-6 items-center gap-1 rounded-pill px-2.25 text-caption font-medium whitespace-nowrap',
                    tag.className
                  )}
                >
                  <TagIcon aria-hidden className="size-3 stroke-2" />
                  {tag.label}
                </span>
              </button>
            );
          })}
          {!visible.length ? (
            <p className="m-0 px-2.5 py-5 text-center text-small text-ink-muted">
              Nenhum membro habilitado encontrado para esta função.
            </p>
          ) : null}
        </div>
        {selected?.issue ? (
          <div
            role="alert"
            className="mx-5 mb-3 flex gap-2.5 rounded-block border border-status-conflict-line bg-danger-soft px-3.5 py-3"
          >
            <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 stroke-[1.8] text-status-conflict" />
            <div className="flex flex-col gap-1.5">
              {[selected.issue, ...selected.rules.filter((r) => r.detail !== selected.issue?.detail)].map(
                (issue) => (
                  <div key={issue.title}>
                    <p className="m-0 text-control font-bold text-status-conflict">{issue.title}</p>
                    <p className="m-0 mt-0.5 text-small text-ink-secondary">{issue.detail}</p>
                  </div>
                )
              )}
            </div>
          </div>
        ) : null}
        <div className="flex justify-end gap-2 border-t border-line-divider px-5 py-3">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          {selected?.issue ? (
            <Button variant="dark" onClick={() => confirmPick(selected)}>
              Escalar mesmo assim
            </Button>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Avisos das regras avançadas da função: "Par restrito", "Em treinamento" (sem alguém
 * experiente junto), "Pouca experiência" e "Pouco tempo de casa". Quem está em treinamento
 * e já tem companhia aparece com o selo neutro; quem completa um par que serve junto, com o
 * selo verde "Serve junto de…".
 */
function RuleBadges({ candidate, partnerName }: { candidate: Candidate; partnerName: string | null }) {
  const accompanied = candidate.trainee && !candidate.rules.some((r) => r.kind === 'trainee');

  if (!candidate.rules.length && !accompanied && !partnerName) return null;

  return (
    <span className="mt-1 flex flex-wrap gap-1">
      {partnerName ? (
        <span className="inline-flex h-5 items-center gap-1 rounded-pill bg-status-complete-bg px-2 text-micro font-semibold text-status-complete">
          <Users aria-hidden className="size-3 stroke-2" />
          Serve junto de {partnerName}
        </span>
      ) : null}
      {accompanied ? (
        <span className="inline-flex h-5 items-center gap-1 rounded-pill bg-surface-muted px-2 text-micro font-semibold text-ink-muted">
          <GraduationCap aria-hidden className="size-3 stroke-2" />
          Em treinamento · acompanhado(a)
        </span>
      ) : null}
      {candidate.rules.map((r) => (
        <span
          key={r.kind}
          title={r.detail}
          className="inline-flex h-5 items-center gap-1 rounded-pill bg-status-partial-bg px-2 text-micro font-semibold text-warning-deep"
        >
          <RuleIcon kind={r.kind} />
          {r.title}
        </span>
      ))}
    </span>
  );
}

const RULE_ICON: Record<RoleRuleKind, typeof CircleCheck> = {
  pair: Users,
  couple: Repeat,
  trainee: GraduationCap,
  experience: History,
  years: CalendarClock,
};

function RuleIcon({ kind }: { kind: RoleRuleKind }) {
  const Icon = RULE_ICON[kind];

  return <Icon aria-hidden className="size-3 stroke-2" />;
}
